import { FC, useCallback } from 'react';
import classNames from 'classnames';

import { CurrencyEnum } from '../../../lib/enums';
import lang from '../../../lib/language';
import {
  formatCurrencyBySymbol,
  formatNumber,
  getCurrencySymbolByCurrencyCode,
  getLimitedText,
  roundPercentValues,
} from '../../../lib/util/DataHelpers';

import CompanyLogo from '../../../components/CompanyLogo/CompanyLogo';
import DiscoverInternalTable from '../../../components/DiscoverInternalTable/DiscoverInternalTable';
import GoBackButton from '../../../components/GoBackButton/GoBackButton';
import Grid from '../../../components/Grid/Grid';
import ClickableDiv from '../../ClickableDiv/ClickableDiv';
import useIsMobile from '../../hooks/useIsMobile';

import { LongTermInvestmentsInternalProps, LongTermInvestmentsRow } from './LongTermInvestmentsInternal.types';
import LongTermInvestmentsSkeletonRow from './LongTermInvestmentsSkeletonRow';

import styles from './LongTermInvestmentsInternal.module.scss';

const HIGH_GROWTH_TAB_INDEX = 1;
const MOBILE_NAME_LIMIT = 14;
const DESKTOP_NAME_LIMIT = 18;
const LOADING_NAME_WIDTH = 134;
const SKELETON_ROW_KEYS = ['lti-skeleton-0', 'lti-skeleton-1', 'lti-skeleton-2', 'lti-skeleton-3'];
const PERCENT_MULTIPLIER = 100;

const LongTermInvestmentsInternal: FC<LongTermInvestmentsInternalProps> = ({
  rows,
  activeTabIndex,
  hasMorePages,
  isViewMoreDisabled,
  fallbackCurrency = CurrencyEnum.USD,
  titleRef,
  onTabChange,
  onViewMoreClick,
  onSymbolClick,
  onGoBack,
}: LongTermInvestmentsInternalProps): JSX.Element => {
  const { isMobile } = useIsMobile();
  const nameLimit = isMobile ? MOBILE_NAME_LIMIT : DESKTOP_NAME_LIMIT;
  const isReduction = activeTabIndex === HIGH_GROWTH_TAB_INDEX;

  // Uppercases the compact average suffixes (k, m, b) for display needs
  const formatCurrencyWithUpperSuffix = useCallback((value: number, currency?: CurrencyEnum) => (
    formatCurrencyBySymbol(value, getCurrencySymbolByCurrencyCode(currency ?? fallbackCurrency), { useSuffix: true })
      .replace(/([kmb])$/, s => s.toUpperCase())
  ), [fallbackCurrency]);

  const renderLegend = (row: LongTermInvestmentsRow) => (isReduction
    ? `${row.growthIndicator}%`
    : `${roundPercentValues(row.dividendYield * PERCENT_MULTIPLIER)}%`);

  const renderList = rows.length === 0
    ? SKELETON_ROW_KEYS.map(key => <LongTermInvestmentsSkeletonRow key={key} />)
    : rows.map((row) => {
      const { symbol, logo, url } = row;
      const displayName = getLimitedText(row.name, nameLimit);

      if (isMobile) {
        return (
          <ClickableDiv
            key={symbol}
            onClick={() => onSymbolClick(symbol)}
            className={styles.wrapperCompanyMob}
          >
            <div className={styles.left}>
              <div className={styles.logoWrapper}>
                {(url || logo) && <CompanyLogo url={url} logo={logo} symbol={symbol} />}
              </div>
              <div className={styles.companyInfo}>
                <span className={styles.symbol}>{symbol}</span>
                <div className={styles.displayName}>{displayName}</div>
              </div>
            </div>
            <div className={styles.right}>{renderLegend(row)}</div>
          </ClickableDiv>
        );
      }

      return (
        <ClickableDiv
          key={symbol}
          onClick={() => onSymbolClick(symbol)}
          className={styles.tableRow}
        >
          <div className={styles.companyTab}>
            <div className={styles.wrapperCompany}>
              {(url || logo) && <CompanyLogo url={url} logo={logo} symbol={symbol} />}
              <div className={styles.companyInfo}>
                {!symbol || !displayName ? (
                  <>
                    <div className={classNames(styles.loadingComponent, styles.loadingComponentMargin)} />
                    <div className={styles.loadingComponent} style={{ width: LOADING_NAME_WIDTH }} />
                  </>
                ) : (
                  <div className={styles.nameAndSymbol}>
                    <div className={styles.symbol}>{symbol}</div>
                    <div className={styles.name}>{displayName}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.tableComponent}>{renderLegend(row)}</div>
          <div className={styles.tableComponent}>
            {formatCurrencyWithUpperSuffix(isReduction ? row.revenue : row.dividendRate, row.currency)}
          </div>
          <div className={styles.tableComponent}>
            {formatCurrencyWithUpperSuffix(isReduction ? row.epsLast12Months : row.marketCap, row.currency)}
          </div>
          <div className={styles.tableComponent}>
            {isReduction
              ? formatCurrencyWithUpperSuffix(row.marketCap, row.currency)
              : formatNumber(row.volume, false, true).toUpperCase()}
          </div>
        </ClickableDiv>
      );
    });

  return (
    <Grid>
      <div className={isMobile ? styles.wrapperMob : styles.wrapper}>
        {!isMobile && (
          <div className={styles.goBackButton}>
            <GoBackButton onBackBtnPress={onGoBack} />
          </div>
        )}
        <h6 ref={titleRef} className={styles.title}>{lang.commonDiscoverSubtitleLongTermInvestments()}</h6>
        <div className={styles.table}>
          <DiscoverInternalTable
            hasViewMoreButton={hasMorePages}
            onViewMoreClick={onViewMoreClick}
            isViewMoreDisabled={isViewMoreDisabled}
            onTabChange={onTabChange}
            tableNames={[lang.commonDiscoverDividendPayers(), lang.commonDiscoverHighGrowthCompanies()]}
            tableHeaders={isReduction ? [
              lang.headerAccountBreakdownSymbolName(),
              lang.commonDiscoverHighGrowthCompaniesLegend(),
              lang.commonDiscoverInfoBoxRevenue(),
              lang.commonDiscoverInfoBoxEps(),
              lang.commonTopGainersAndLosersMarketCap(),
            ] : [
              lang.headerAccountBreakdownSymbolName(),
              lang.commonDiscoverDividendPayersLegend(),
              lang.commonDiscoverInfoBoxDividendRate(),
              lang.commonTopGainersAndLosersMarketCap(),
              lang.commonDiscoverInfoBoxVolume(),
            ]}
            renderList={renderList}
            initialActiveTab={activeTabIndex}
          />
        </div>
      </div>
    </Grid>
  );
};

export default LongTermInvestmentsInternal;
